import React from "react";
import { useNavigate } from "react-router-dom";
import { HubModuleVisual } from "./HubModuleVisual";
import { HOME_THEME as T } from "../../lib/homeTheme";

export interface HubEditorialItem {
  id: string;
  title: string;
  description: string;
  path: string;
  accent?: "lavender" | "mint";
}

interface HubEditorialGridProps {
  items: HubEditorialItem[];
  /** Columnas en desktop (por defecto 3, como Explorar). */
  columns?: 1 | 2 | 3;
}

const ACCENT_BORDER: Record<NonNullable<HubEditorialItem["accent"]>, string> = {
  lavender: T.lavender,
  mint: T.mint,
};

const COL_CLASS: Record<NonNullable<HubEditorialGridProps["columns"]>, string> = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
};

export const HubEditorialGrid: React.FC<HubEditorialGridProps> = ({
  items,
  columns = 3,
}) => {
  const navigate = useNavigate();

  return (
    <div className={`grid grid-cols-1 ${COL_CLASS[columns]} gap-x-12 gap-y-16`}>
      {items.map((item, i) => {
        const accent = item.accent ?? (i % 2 === 0 ? "lavender" : "mint");
        const accentColor = ACCENT_BORDER[accent];

        return (
          <button
            key={item.id}
            type="button"
            onClick={() => navigate(item.path)}
            className="group cursor-pointer flex flex-col gap-4 relative w-full bg-transparent text-left"
            style={{ "--hub-accent": accentColor } as React.CSSProperties}
          >
            <div className="flex justify-between items-center border-t border-white/10 pt-4 mb-2 transition-colors duration-300 group-hover:border-[var(--hub-accent)]">
              <span className="text-xs text-white/40 font-mono tracking-widest tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span
                className="text-lg font-light text-white/40 transition-transform duration-300 group-hover:translate-x-1"
                aria-hidden
              >
                →
              </span>
            </div>

            <div className="flex items-center justify-between gap-6">
              <h2 className="min-w-0 flex-1 text-4xl font-serif text-white/90 group-hover:text-white transition-colors leading-[1.05] tracking-tight">
                {item.title}
              </h2>
              <HubModuleVisual moduleId={item.id} />
            </div>

            <p className="text-sm text-white/50 font-light leading-relaxed">{item.description}</p>
          </button>
        );
      })}
    </div>
  );
};
